import { ref } from "vue"
import { Core, ProfileTypes, Citizen, GeorgiaDfcsBenefits } from "@minicity/core"
import { FormNavState, Section, useForm } from "@/composables/useForm";
import { sessionState } from "@/core/session";
import { DeepPartial, generateRandomId, notifyError, notifyNetworkError, timestampToYYYYMMDD } from "@/core/utils";
import { apiPost } from "@/core/api";
import { submitApplication } from "./api";

// Define all pages in the form
export type Page = 
    "introVideo"
    | "selectBenefitTypes"
    | "tanfWaiver" | "generalWaiver" 
    | "authorizedRepresentative" | "authorizedRepresentative2" | "authorizedRepresentativeSettings"
    | "basicCitizenInfo" | "address" | "contactInfo" | "communicationPreferences"
    | "householdMembers"
    | "foodStampsQualificationQuestions" | "additionalQualificationQuestions"
    | "income"
    | "careExpenses" | "dependentTransportationExpenses" | "medicalExpenses" | "medicalTransportationExpenses" | "childSupportExpenses" | "shelterOrUtilityExpenses" | "sharedExpenses" | "otherBillPayer"
    | "supportDocGuidance" | "supportDocUpload"
    | "equifaxConsent"
    | "notificationPreferences"
    | "signature"
    | "preSubmit" | "completed";

// Group pages into sections
export const sections:Section<Page>[] = [
    {name: "Let's Get Started!", pages: [
        "introVideo", "selectBenefitTypes"
    ]},
    {name: "Important Terms", pages: [
        "tanfWaiver", "generalWaiver"
    ]},
    {name: "Authorized Representatives", pages: [
        "authorizedRepresentative", "authorizedRepresentative2", "authorizedRepresentativeSettings"
    ]},
    {name: "About You", pages: [
        "basicCitizenInfo", "address", "contactInfo", "communicationPreferences"
    ]},
    {name: "About Your Household", pages: [
        "householdMembers"
    ]},
    {name: "Qualifying Questions", pages: [
        "foodStampsQualificationQuestions", "additionalQualificationQuestions"
    ]},
    {name: "Income", pages: [
        "income"
    ]},
    {name: "Expenses", pages: [
        "careExpenses", "dependentTransportationExpenses", "medicalExpenses", "medicalTransportationExpenses", "childSupportExpenses", "shelterOrUtilityExpenses", "sharedExpenses", "otherBillPayer"
    ]},
    {name: "Supporting Documentation / ID", pages: [
        "supportDocGuidance", "supportDocUpload"
    ]},
    {name: "Final Questions", pages: [
        "equifaxConsent", "notificationPreferences"
    ]},
    {name: "Sign and Certify", pages: [
        "signature"
    ]},
    {name: "Submit", pages: [
        "preSubmit"
    ]},
];

export interface SupportDocument {
    type: string,
    front: Core.Document,
    back?: Core.Document
}

export interface ModuleState {
    application: DeepPartial<GeorgiaDfcsBenefits.FormData>,
    supportDocuments: SupportDocument[],
    signatures: {[key: string]: Core.Document},
    notificationPreferences: Citizen.NotificationPreferences,

    formNavState: FormNavState<Page>,
    miscState: any,
}

export const moduleState = ref<ModuleState|null>(null);

export const initNewApplication = (initialMiscState: any):ModuleState => {
    return {
        application: initFormData(),
        supportDocuments: [],
        signatures: {},
        notificationPreferences: sessionState.session!.user.preferences.notifications,

        formNavState: {
            activePage: "selectBenefitTypes",
            previousPages: [],
            errors: [],
        },

        miscState: initialMiscState
    };
};

export const initFormData = ():DeepPartial<GeorgiaDfcsBenefits.FormData> => {
    // Pre-populate any fields we can from the citizen's profile, as well as any other defaults
    const profile = sessionState.session!.user.profile;
    return {
        selectedBenefits: {},
        voterRegistrationIntent: false,
        authorizedRepresentatives: {},
        applicant: {
            firstName: profile.firstName,
            middleInitial: profile.middleName ? profile.middleName.substring(0,1) : "",
            lastName: profile.lastName,
            socialSecurityNumber: profile.socialSecurityNumber,
            dateOfBirth: timestampToYYYYMMDD(profile.dateOfBirth),
            isApplyingForBenefits: true,
            preferredLanguage: "English",
            useCareProviderAddressForMailing: true,
            useCareProviderAddressForStreet: true,
        },
        householdMembers: [],
        additionalQualifications: {},
        income: [],
        expenses: {
            careExpenses: [],
            medicalExpenses: [],
            medicalTransportationExpenses: [],
            childSupportExpenses: [],
        },
    };
}

export const submitModule = async (draftApplicationId: string) => {  
    // Collate all the required info for the application, and submit it to the server
    const mappedSupportDocs:Core.Document[] = [];
    for (const doc of moduleState.value!.supportDocuments) {
        mappedSupportDocs.push({id: doc.front.id, filename: `${doc.type} - front - ${doc.front.filename}`});
        if (doc.back) {
            mappedSupportDocs.push({id: doc.back.id, filename: `${doc.type} - back - ${doc.back.filename}`});
        }
    }
    
    return submitApplication({
        citizenId: sessionState.session!.user.id,
        formData: moduleState.value!.application! as GeorgiaDfcsBenefits.FormData,
        supportDocuments: mappedSupportDocs,
        signatures: moduleState.value!.signatures,
        notificationPreferences: moduleState.value!.notificationPreferences,
        idempotencyToken: draftApplicationId,
        draftApplicationId: draftApplicationId,
    });
};

/*
 * Types used in the form
 */
export const EquifaxConsentOptions = {
    "Yes, my data can be shared": true,
    "No, do not share my data": false
}